import { Controller } from "@hotwired/stimulus"
import { Chart, BarController, BarElement, LinearScale, CategoryScale, Tooltip, Legend } from "chart.js"

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Tooltip, Legend)

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    labels: Array,
    datasets: Array,
    deliveredCount: Number
  }

  connect() {
    const deliveredCount = this.deliveredCountValue

    this.chart = new Chart(this.canvasTarget, {
      type: "bar",
      data: {
        labels: this.labelsValue,
        datasets: this.datasetsValue.map(ds => ({
          ...ds,
          barPercentage: 0.9,
          categoryPercentage: 0.9
        }))
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            grid: { color: "rgba(0, 0, 0, 0.05)" },
            ticks: {
              precision: 0,
              font: { size: 11 },
              color: "rgba(0, 0, 0, 0.4)"
            }
          },
          y: {
            stacked: true,
            grid: { display: false },
            ticks: {
              font: { size: 12, weight: "500" },
              color: "rgba(0, 0, 0, 0.7)"
            },
          }
        },
        layout: {
          padding: { top: 10, right: 45 }
        },
        plugins: {
          legend: {
            position: "top",
            align: "center",
            labels: {
              padding: 16,
              usePointStyle: true,
              pointStyle: "rect",
              font: { size: 11 },
              generateLabels: (chart) => {
                return chart.data.datasets.map((ds, i) => {
                  const colors = ds.backgroundColor
                  // Use the second color (first engagement row) for legend swatches
                  const color = Array.isArray(colors) ? (colors[1] || colors[0]) : colors
                  return {
                    text: ds.label,
                    fillStyle: color,
                    strokeStyle: "transparent",
                    lineWidth: 0,
                    hidden: !chart.isDatasetVisible(i),
                    datasetIndex: i
                  }
                })
              }
            }
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: 12,
            titleFont: { size: 13 },
            bodyFont: { size: 12 },
            callbacks: {
              label: (ctx) => {
                const count = ctx.raw
                if (deliveredCount > 0 && count > 0) {
                  const pct = ((count / deliveredCount) * 100).toFixed(1)
                  return ` ${ctx.dataset.label}: ${count} (${pct}%)`
                }
                return ` ${ctx.dataset.label}: ${count}`
              }
            }
          }
        }
      },
      plugins: [{
        id: "barPercentLabels",
        afterDatasetsDraw(chart) {
          const ctx = chart.ctx
          const numBars = chart.data.labels.length
          const numDatasets = chart.data.datasets.length

          for (let barIdx = 0; barIdx < numBars; barIdx++) {
            // Sum across all datasets for this bar
            let total = 0
            for (let dsIdx = 0; dsIdx < numDatasets; dsIdx++) {
              total += chart.data.datasets[dsIdx].data[barIdx] || 0
            }

            if (total === 0 || deliveredCount === 0) continue

            // Find the rightmost edge of the stacked bar
            let maxX = 0
            for (let dsIdx = 0; dsIdx < numDatasets; dsIdx++) {
              const meta = chart.getDatasetMeta(dsIdx)
              const bar = meta.data[barIdx]
              if (bar && bar.x > maxX) maxX = bar.x
            }

            const pct = barIdx === 0 ? "100%" : `${Math.round((total / deliveredCount) * 100)}%`
            const bar = chart.getDatasetMeta(0).data[barIdx]

            ctx.save()
            ctx.font = "600 12px sans-serif"
            ctx.fillStyle = "rgba(0, 0, 0, 0.5)"
            ctx.textAlign = "left"
            ctx.textBaseline = "middle"
            ctx.fillText(pct, maxX + 8, bar.y)
            ctx.restore()
          }
        }
      }]
    })
  }

  disconnect() {
    if (this.chart) this.chart.destroy()
  }
}
